import { useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  addRfq,
  answerRfq,
  getProcurement,
  recordDelivery,
  recordMaterialInspection,
  recordPurchaseOrder,
  type ProcurementView,
} from '../api.js';
import { formatDate, formatMoney } from '../labels.js';
import { Attachments } from './Attachments.js';

/**
 * The procurement panel for ONE material: RFQs, the PO, deliveries, and
 * QA/QC's verdict on what arrived. Opened from the material's row.
 *
 * The buttons follow the acts, not one blanket permission:
 *   - RFQs follow `send_rfq` (the supplier conversation is procurement's)
 *   - the PO follows `order` — and recording it IS the order
 *   - deliveries follow `record_delivery` (`04` gives a Site Engineer VC:
 *     the person at the gate when the truck arrives)
 *   - the verdict follows `inspect`, and the SERVER narrows it further to
 *     QA/QC through the same machine as the register's own buttons
 */

const RFQ_WORDS: Readonly<Record<string, string>> = {
  sent: 'Waiting',
  quoted: 'Quoted',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
};

const RESULT_WORDS: Readonly<Record<string, string>> = {
  accepted: 'Accepted',
  accepted_with_notes: 'Accepted with notes',
  rejected: 'Rejected',
};

interface Props {
  readonly projectId: string;
  readonly materialId: string;
  readonly can: {
    readonly rfq: boolean;
    readonly order: boolean;
    readonly deliver: boolean;
    readonly inspect: boolean;
  };
  /** Ask the register to re-read itself — a PO or a verdict moves the material. */
  readonly onMaterialChanged: () => void;
}

export function Procurement({ projectId, materialId, can, onMaterialChanged }: Props): JSX.Element {
  const [view, setView] = useState<ProcurementView | null>(null);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState<'rfq' | 'po' | 'delivery' | 'verdict' | null>(null);
  const [quoting, setQuoting] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await getProcurement(projectId, materialId));
      setDenied(false);
    } catch (caught) {
      const refused =
        caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load the procurement records.');
    }
  }, [projectId, materialId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(
    work: () => Promise<ProcurementView>,
    movesMaterial = false,
  ): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setView(await work());
      if (movesMaterial) onMaterialChanged();
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (denied) {
    return <p className="hint">Your role does not include this material&rsquo;s procurement.</p>;
  }
  if (!view) {
    return error ? (
      <div className="alert" role="alert">
        <span>{error}</span>
      </div>
    ) : (
      <p aria-live="polite">Loading the procurement records…</p>
    );
  }

  return (
    <div className="procurement">
      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      <div className="button-row">
        {can.rfq && !adding && (
          <button type="button" className="link-button" onClick={() => setAdding('rfq')}>
            Ask a supplier to quote
          </button>
        )}
        {can.order && !adding && (
          <button type="button" className="link-button" onClick={() => setAdding('po')}>
            Record the PO
          </button>
        )}
        {can.deliver && !adding && (
          <button type="button" className="link-button" onClick={() => setAdding('delivery')}>
            Record a delivery
          </button>
        )}
        {can.inspect && !adding && (
          <button type="button" className="link-button" onClick={() => setAdding('verdict')}>
            Record the verdict
          </button>
        )}
      </div>

      {adding === 'rfq' && (
        <RfqForm
          busy={busy}
          onCancel={() => setAdding(null)}
          onSubmit={async (input) => {
            if (await run(() => addRfq(projectId, materialId, input))) setAdding(null);
          }}
        />
      )}
      {adding === 'po' && (
        <PoForm
          busy={busy}
          onCancel={() => setAdding(null)}
          onSubmit={async (input) => {
            if (await run(() => recordPurchaseOrder(projectId, materialId, input), true)) {
              setAdding(null);
            }
          }}
        />
      )}
      {adding === 'delivery' && (
        <DeliveryForm
          busy={busy}
          onCancel={() => setAdding(null)}
          onSubmit={async (input) => {
            if (await run(() => recordDelivery(projectId, materialId, input))) setAdding(null);
          }}
        />
      )}
      {adding === 'verdict' && (
        <VerdictForm
          busy={busy}
          onCancel={() => setAdding(null)}
          onSubmit={async (input) => {
            if (await run(() => recordMaterialInspection(projectId, materialId, input), true)) {
              setAdding(null);
            }
          }}
        />
      )}

      {view.rfqs.length + view.purchase_orders.length + view.deliveries.length +
        view.inspections.length ===
        0 && (
        <p className="hint">
          <strong>Nothing recorded yet.</strong> RFQs, the PO, deliveries and the inspection
          verdict all land here as they happen.
        </p>
      )}

      {view.rfqs.length > 0 && (
        <>
          <h5 className="sub-head">Quotes</h5>
          <ul className="task-list">
            {view.rfqs.map((rfq) => (
              <li key={rfq.id} className="task-row">
                <div className="task-main">
                  <span className="task-title">
                    {rfq.supplier_name}
                    <span className="tree-code">{' · '}{RFQ_WORDS[rfq.status] ?? rfq.status}</span>
                  </span>
                  <span className="task-meta">
                    <span>asked {formatDate(rfq.sent_on)}</span>
                    {rfq.responded_on && <span>answered {formatDate(rfq.responded_on)}</span>}
                    {'quoted_amount' in rfq && rfq.quoted_amount && (
                      <span className="mono">{formatMoney(rfq.quoted_amount)}</span>
                    )}
                    {rfq.note && <span>{rfq.note}</span>}
                  </span>
                  {quoting === rfq.id && (
                    <QuoteForm
                      busy={busy}
                      onCancel={() => setQuoting(null)}
                      onSubmit={async (input) => {
                        if (await run(() => answerRfq(projectId, rfq.id, input))) setQuoting(null);
                      }}
                    />
                  )}
                </div>
                {can.rfq && rfq.status === 'sent' && quoting !== rfq.id && (
                  <span className="row-actions">
                    <button
                      type="button"
                      className="link-button"
                      disabled={busy}
                      onClick={() => setQuoting(rfq.id)}
                    >
                      Record the answer
                      <span className="visually-hidden"> from {rfq.supplier_name}</span>
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {view.purchase_orders.length > 0 && (
        <>
          <h5 className="sub-head">Purchase order</h5>
          <ul className="task-list">
            {view.purchase_orders.map((po) => (
              <li key={po.id} className="task-row">
                <div className="task-main">
                  <span className="task-title">
                    <span className="mono">{po.po_number}</span>
                    {po.status === 'cancelled' && <span className="tree-code">{' · '}Cancelled</span>}
                  </span>
                  <span className="task-meta">
                    <span>{formatDate(po.po_date)}</span>
                    {po.supplier_name && <span>{po.supplier_name}</span>}
                    {'po_value' in po && po.po_value && (
                      <span className="mono">{formatMoney(po.po_value)}</span>
                    )}
                    {po.note && <span>{po.note}</span>}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {view.deliveries.length > 0 && (
        <>
          <h5 className="sub-head">Deliveries</h5>
          <ul className="task-list">
            {view.deliveries.map((delivery) => (
              <li key={delivery.id} className="task-row">
                <div className="task-main">
                  <span className="task-title">
                    {formatDate(delivery.delivered_on)}
                    {delivery.quantity_received && (
                      <span className="tree-code">{' · '}{delivery.quantity_received} received</span>
                    )}
                  </span>
                  <span className="task-meta">
                    {delivery.received_by_name && (
                      <span>received by {delivery.received_by_name}</span>
                    )}
                    {/* Damage in words, on the row — the reason this log exists. */}
                    {delivery.damage_notes && (
                      <span className="task-overdue">Damage: {delivery.damage_notes}</span>
                    )}
                    {delivery.note && <span>{delivery.note}</span>}
                  </span>
                  <Attachments
                    projectId={projectId}
                    entityType="delivery"
                    entityId={delivery.id}
                    can={{ write: can.deliver }}
                    noun="delivery photos"
                  />
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {view.inspections.length > 0 && (
        <>
          <h5 className="sub-head">Inspection verdicts</h5>
          <ul className="task-list">
            {view.inspections.map((inspection) => (
              <li key={inspection.id} className="task-row">
                <div className="task-main">
                  <span className="task-title">
                    {RESULT_WORDS[inspection.result]}
                    <span className="tree-code">{' · '}{formatDate(inspection.inspected_on)}</span>
                  </span>
                  <span className="task-meta">
                    {inspection.inspector_name && <span>by {inspection.inspector_name}</span>}
                    {inspection.notes && <span>{inspection.notes}</span>}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function RfqForm({
  busy,
  onCancel,
  onSubmit,
}: {
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: { supplier_name: string; sent_on: string }) => void | Promise<void>;
}): JSX.Element {
  const [name, setName] = useState('');
  const [date, setDate] = useState('');

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!name.trim() || !date) return;
    void onSubmit({ supplier_name: name.trim(), sent_on: date });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <p className="panel-title">Ask a supplier to quote</p>
      <div className="field-row">
        <div className="field">
          <label htmlFor="rfq-supplier">Supplier</label>
          <input
            id="rfq-supplier"
            value={name}
            maxLength={200}
            required
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="rfq-sent">Sent on</label>
          <input
            id="rfq-sent"
            type="date"
            value={date}
            required
            disabled={busy}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
      </div>
      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !name.trim() || !date}>
          Record
        </button>
        <button type="button" className="secondary inline" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function QuoteForm({
  busy,
  onCancel,
  onSubmit,
}: {
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    status: string;
    quoted_amount?: string | null;
    responded_on?: string | null;
  }) => void | Promise<void>;
}): JSX.Element {
  const [status, setStatus] = useState('quoted');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');

  const needsFigure = status === 'quoted';
  const valid = !needsFigure || (amount.trim() !== '' && date !== '');

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!valid) return;
    void onSubmit(
      needsFigure
        ? { status, quoted_amount: amount.replace(/,/g, '').trim(), responded_on: date }
        : { status },
    );
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="field-row">
        <div className="field">
          <label htmlFor="quote-status">What came back?</label>
          <select
            id="quote-status"
            value={status}
            disabled={busy}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="quoted">A quote</option>
            <option value="declined">They declined</option>
            <option value="withdrawn">We withdrew</option>
          </select>
        </div>
        {needsFigure && (
          <>
            <div className="field">
              <label htmlFor="quote-amount">Amount</label>
              <input
                id="quote-amount"
                inputMode="decimal"
                value={amount}
                disabled={busy}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="quote-date">On</label>
              <input
                id="quote-date"
                type="date"
                value={date}
                disabled={busy}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </>
        )}
      </div>
      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !valid}>
          Record
        </button>
        <button type="button" className="secondary inline" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function PoForm({
  busy,
  onCancel,
  onSubmit,
}: {
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    po_number: string;
    po_date: string;
    po_value?: string | null;
    supplier_name?: string | null;
  }) => void | Promise<void>;
}): JSX.Element {
  const [number, setNumber] = useState('');
  const [date, setDate] = useState('');
  const [value, setValue] = useState('');
  const [supplier, setSupplier] = useState('');

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!number.trim() || !date) return;
    void onSubmit({
      po_number: number.trim(),
      po_date: date,
      ...(value.trim() ? { po_value: value.replace(/,/g, '').trim() } : {}),
      ...(supplier.trim() ? { supplier_name: supplier.trim() } : {}),
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <p className="panel-title">Record the purchase order</p>
      <p className="hint">
        Recording the PO IS ordering — the material becomes &ldquo;ordered&rdquo; with it. It is
        refused on a material that is not ready to order.
      </p>
      <div className="field-row">
        <div className="field">
          <label htmlFor="po-number">PO number</label>
          <input
            id="po-number"
            value={number}
            maxLength={60}
            required
            disabled={busy}
            onChange={(e) => setNumber(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="po-date">Dated</label>
          <input
            id="po-date"
            type="date"
            value={date}
            required
            disabled={busy}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="po-value">Value (optional)</label>
          <input
            id="po-value"
            inputMode="decimal"
            value={value}
            disabled={busy}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="po-supplier">Supplier (optional)</label>
          <input
            id="po-supplier"
            value={supplier}
            maxLength={200}
            disabled={busy}
            onChange={(e) => setSupplier(e.target.value)}
          />
        </div>
      </div>
      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !number.trim() || !date}>
          Record and order
        </button>
        <button type="button" className="secondary inline" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function DeliveryForm({
  busy,
  onCancel,
  onSubmit,
}: {
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    delivered_on: string;
    quantity_received?: string | null;
    damage_notes?: string | null;
  }) => void | Promise<void>;
}): JSX.Element {
  const [date, setDate] = useState('');
  const [quantity, setQuantity] = useState('');
  const [damage, setDamage] = useState('');

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!date) return;
    void onSubmit({
      delivered_on: date,
      ...(quantity.trim() ? { quantity_received: quantity.trim() } : {}),
      ...(damage.trim() ? { damage_notes: damage.trim() } : {}),
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <p className="panel-title">Record a delivery</p>
      <p className="hint">
        The status does not move — deliveries are partial more often than not, and
        &ldquo;at site&rdquo; stays your call on the register, with this log as the evidence.
      </p>
      <div className="field-row">
        <div className="field">
          <label htmlFor="del-date">Arrived on</label>
          <input
            id="del-date"
            type="date"
            value={date}
            required
            disabled={busy}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="del-qty">Quantity received (optional)</label>
          <input
            id="del-qty"
            inputMode="decimal"
            value={quantity}
            disabled={busy}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </div>
      </div>
      <div className="field">
        <label htmlFor="del-damage">Damage or shortage (leave blank if none)</label>
        <textarea
          id="del-damage"
          rows={2}
          maxLength={2000}
          value={damage}
          disabled={busy}
          onChange={(e) => setDamage(e.target.value)}
        />
      </div>
      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !date}>
          Record
        </button>
        <button type="button" className="secondary inline" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function VerdictForm({
  busy,
  onCancel,
  onSubmit,
}: {
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    result: string;
    inspected_on: string;
    notes?: string | null;
  }) => void | Promise<void>;
}): JSX.Element {
  const [result, setResult] = useState('accepted');
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');

  const needsReason = result === 'rejected';
  const valid = date !== '' && (!needsReason || notes.trim() !== '');

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!valid) return;
    void onSubmit({
      result,
      inspected_on: date,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <p className="panel-title">The inspection verdict</p>
      <p className="hint">
        Only QA/QC records this — it moves the material to inspected or rejected in the same
        act.
      </p>
      <div className="field-row">
        <div className="field">
          <label htmlFor="ver-result">Verdict</label>
          <select
            id="ver-result"
            value={result}
            disabled={busy}
            onChange={(e) => setResult(e.target.value)}
          >
            <option value="accepted">Accepted</option>
            <option value="accepted_with_notes">Accepted with notes</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="ver-date">On</label>
          <input
            id="ver-date"
            type="date"
            value={date}
            required
            disabled={busy}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
      </div>
      <div className="field">
        <label htmlFor="ver-notes">
          Notes{needsReason ? ' — a rejection needs the reason' : ' (optional)'}
        </label>
        <textarea
          id="ver-notes"
          rows={2}
          maxLength={2000}
          value={notes}
          disabled={busy}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !valid}>
          Record
        </button>
        <button type="button" className="secondary inline" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
